import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Select,
  Spinner,
} from "../components/ui.js";
import {
  useContactMessages,
  useDeleteContactMessage,
  useSetContactHandled,
  type ContactFilters,
} from "../hooks/useContact.js";
import { formatDate } from "../lib/format.js";

const DEFAULT_FILTERS: ContactFilters = { q: "", status: "open" };

/** Read and triage messages submitted through the public contact form. */
export function ContactPage() {
  const [filters, setFilters] = useState<ContactFilters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");

  const messagesQuery = useContactMessages(filters);
  const setHandled = useSetContactHandled();
  const deleteMessage = useDeleteContactMessage();

  // Debounce the search box into the query filters.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setFilters((previous) => (previous.q === searchInput ? previous : { ...previous, q: searchInput }));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const messages = messagesQuery.data?.data ?? [];
  const openCount = messagesQuery.data?.meta.open ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Messages</h1>
          <p className="text-sm text-slate-400">
            Contact-form submissions from the public site.
            {openCount > 0 ? (
              <>
                {" "}
                <span className="text-amber-300">{openCount} open</span>.
              </>
            ) : null}
          </p>
        </div>
      </div>

      <Card className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Search">
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Name, email or message…"
            />
          </Field>
          <Field label="Status">
            <Select
              value={filters.status}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  status: event.target.value as ContactFilters["status"],
                }))
              }
            >
              <option value="open">Open</option>
              <option value="handled">Handled</option>
              <option value="all">All</option>
            </Select>
          </Field>
        </div>
      </Card>

      {messagesQuery.isError ? (
        <ErrorBanner message={(messagesQuery.error as Error).message} />
      ) : null}
      {setHandled.isError ? <ErrorBanner message={(setHandled.error as Error).message} /> : null}
      {deleteMessage.isError ? (
        <ErrorBanner message={(deleteMessage.error as Error).message} />
      ) : null}

      {messagesQuery.isLoading ? (
        <Spinner label="Loading messages…" />
      ) : messages.length === 0 ? (
        <EmptyState
          title={filters.status === "open" ? "No open messages" : "No messages"}
          hint="When someone uses the contact form on the public site, it lands here."
        />
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <Card key={message.id} className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-100">{message.name}</p>
                  <a
                    href={`mailto:${message.email}`}
                    className="text-sm text-indigo-300 hover:text-indigo-200"
                  >
                    {message.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  {message.handledAt ? (
                    <Badge tone="green">Handled</Badge>
                  ) : (
                    <Badge tone="amber">Open</Badge>
                  )}
                  <span className="text-xs text-slate-400">{formatDate(message.createdAt)}</span>
                </div>
              </div>

              <p className="whitespace-pre-wrap text-sm text-slate-300">{message.message}</p>

              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="secondary"
                  disabled={setHandled.isPending}
                  onClick={() => setHandled.mutate({ id: message.id, handled: !message.handledAt })}
                >
                  {message.handledAt ? "Reopen" : "Mark handled"}
                </Button>
                <Button
                  variant="danger"
                  disabled={deleteMessage.isPending}
                  onClick={() => {
                    if (window.confirm(`Delete the message from ${message.name}?`)) {
                      deleteMessage.mutate(message.id);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
